// "Sign in with Google" web flow.
//
// Mirrors the appleAuth.ts pattern — three small, testable functions:
//   1. loadGoogleSdk()     — inject Google's <script> tag once, return a
//                            cached promise so concurrent / repeat callers
//                            don't re-inject.
//   2. signInWithGoogle()  — open the One Tap / popup prompt, exchange the
//                            resulting id_token at the backend, return
//                            {token, user}.
//
// Unlike Apple, Google doesn't need a separate init() call before each
// sign-in — initialize() is called inside signInWithGoogle() with the
// per-attempt callback.

import { StoredUser } from "../utils/authStorage";
import { GOOGLE_SDK_URL, getGoogleClientId } from "../config/googleAuthConfig";
import { apiFetch } from "./apiClient";

let sdkPromise: Promise<void> | null = null;

export const __resetGoogleAuthForTests = (): void => {
  sdkPromise = null;
};

export const loadGoogleSdk = (): Promise<void> => {
  if (sdkPromise) return sdkPromise;
  if (typeof document === "undefined") {
    return Promise.reject(new Error("Google SDK can only load in a browser"));
  }

  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_SDK_URL}"]`
    );
    if (existing && window.google?.accounts) {
      resolve();
      return;
    }

    const script = existing ?? document.createElement("script");
    script.src = GOOGLE_SDK_URL;
    script.async = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => {
      sdkPromise = null;
      reject(new Error("Failed to load Google Sign-In SDK"));
    });
    if (!existing) document.head.appendChild(script);
  });

  return sdkPromise;
};

export interface GoogleSignInResult {
  token: string;
  user: StoredUser;
}

interface AuthBackendResponse {
  access_token: string;
  name?: string | null;
  email?: string | null;
}

export const signInWithGoogle = (): Promise<GoogleSignInResult> => {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts) {
      reject(new Error("Google SDK is not loaded"));
      return;
    }

    const clientId = getGoogleClientId();
    if (!clientId) {
      reject(new Error("Google Sign-In is not configured for this hostname."));
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: GoogleCredentialResponse) => {
        const idToken = response.credential;
        if (!idToken) {
          reject(new Error("Google did not return a credential"));
          return;
        }

        try {
          const result = await apiFetch<AuthBackendResponse>(
            "/v1/auth/signin/google_web",
            {
              method: "POST",
              body: { identity_token: idToken },
            }
          );

          if (!result?.access_token) {
            reject(new Error("Auth backend did not return an access_token"));
            return;
          }

          resolve({
            token: result.access_token,
            user: {
              name: result.name ?? "",
              email: result.email ?? "",
            },
          });
        } catch (err) {
          reject(err);
        }
      },
    });

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        reject(new Error("google_prompt_dismissed"));
      }
    });
  });
};
